import React, { useState, useMemo, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useLocalStorage, Vertical, Horizontal, NothingMessage } from '@gravitate-js/excalibrr'
import { Switch } from 'antd'
import { extractAndDisplayError } from '../../../../../utils/errorUtils'
import { useSupplierReconciliation } from '../../../../../api/useSupplierReconciliation'
import { useMatchActions } from '../../hooks/useMatchActions'
import { useQueryClient } from '@tanstack/react-query'
import { useModelConfig } from '../../../../../hooks/useModelConfig'
import { MatchDetailSidebar } from './MatchDetailSidebar'
import { MatchSummaryGrid } from './MatchSummaryGrid'
import { TaxGridsSection } from './TaxGridsSection'
import { ManageMatchSection } from './ManageMatchSection'
import { MatchDetailNavigationHeader } from './MatchDetailNavigationHeader'
import { useMasterMetadata } from '../../../../../api/useMetadata/useMasterMetadata'
import type { SupplierReconciliationMetadataResponse } from '../../../../../api/useMetadata/types'
import { PriceBreakdownDrawer } from './PriceBreakdownDrawer'

export function MatchDetailContent() {
  const { id } = useParams()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { useMatchDetail } = useSupplierReconciliation()

  // Fetch metadata for reason codes and timezone resolution
  const metadataQuery = useMasterMetadata<SupplierReconciliationMetadataResponse>('supplier_reconciliation')

  // Handle metadata errors
  useEffect(() => {
    if (metadataQuery.error) {
      extractAndDisplayError(metadataQuery.error, 'Failed to load configuration data')
    }
  }, [metadataQuery.error])

  // Create timezone resolver from metadata (same logic as MatchView)
  const getTimezoneForMatch = useMemo(() => {
    if (!metadataQuery.data?.terminals) {
      return () => undefined
    }

    // Build terminal name to timezone mapping
    const terminalTimezoneMap = new Map<string, string>()
    metadataQuery.data.terminals.forEach((terminal) => {
      if (terminal.timezone) {
        // Map by both full name and short_name if available
        terminalTimezoneMap.set(terminal.name.toLowerCase(), terminal.timezone)
        if (terminal.short_name) {
          terminalTimezoneMap.set(terminal.short_name.toLowerCase(), terminal.timezone)
        }
      }
    })

    return (terminals?: string[]): string | undefined => {
      if (!terminals || terminals.length === 0) return undefined

      // Priority order: prefer terminals that likely correspond to BOL source
      // Shell terminals are typically source terminals for BOLs
      const priorityPatterns = ['shell/', 'shell', 'sarnia']

      for (const pattern of priorityPatterns) {
        for (const terminal of terminals) {
          if (terminal.toLowerCase().includes(pattern)) {
            const normalizedName = terminal.toLowerCase().trim()
            const timezone = terminalTimezoneMap.get(normalizedName)
            if (timezone) {
              return timezone
            }
          }
        }
      }

      // Fallback: use first terminal with timezone (original logic)
      for (const terminal of terminals) {
        const normalizedName = terminal.toLowerCase().trim()
        const timezone = terminalTimezoneMap.get(normalizedName)
        if (timezone) {
          return timezone
        }
      }
      return undefined
    }
  }, [metadataQuery.data?.terminals])

  // Fetch model config for delivery reconciliation settings
  const { config: modelConfig, isLoading: modelConfigLoading, error: modelConfigError } = useModelConfig()

  // Handle model config errors
  useEffect(() => {
    if (modelConfigError) {
      extractAndDisplayError(modelConfigError, 'Failed to load model configuration')
    }
  }, [modelConfigError])

  // Handler for refreshing match data after mapping changes
  const handleMappingRefresh = () => {
    if (id && id !== 'no-match-id') {
      queryClient.invalidateQueries({
        queryKey: [
          'post',
          '/supplier_rec/match/detail',
          {
            params: {
              query: {
                match_id: id,
              },
            },
          },
        ],
      })
      queryClient.invalidateQueries({
        queryKey: ['post', '/supplier_rec/match/detail'],
      })
    }
  }

  // Get match actions for hold/approve functionality
  const {
    handleIndividualHold,
    handleIndividualUnhold,
    handleIndividualUnapprove,
    handleIndividualApprove,
    handleVoidInvoice,
    handleUnvoidInvoice,
    approveMatchMutation,
    waitingOnSupplierMutation,
    voidInvoiceMutation,
    unvoidInvoiceMutation,
  } = useMatchActions({
    onRefetch: handleMappingRefresh,
  })

  // Try to get match data from navigation state first
  const matchDataFromState = location.state?.matchData
  const noMatchId = location.state?.noMatchId

  // Fetch if we have a valid ID (not placeholder), regardless of state data
  const shouldFetch = !!id && id !== 'no-match-id'

  const {
    data: fetchedMatchDetails,
    isLoading: isFetchingMatch,
    error: fetchError,
  } = useMatchDetail(shouldFetch ? id : undefined)

  // Handle fetch errors using React Query v5 pattern
  useEffect(() => {
    if (fetchError && shouldFetch) {
      extractAndDisplayError(fetchError, 'Failed to load match details')
    }
  }, [fetchError, shouldFetch])

  // Use fetched data when we have a real ID, otherwise use state data
  const matchDetails = shouldFetch ? fetchedMatchDetails : matchDataFromState
  const isLoading = shouldFetch ? isFetchingMatch : false

  // Check if we have the no match ID flag or if fetch failed
  const hasNoValidMatchId = noMatchId || id === 'no-match-id' || (!matchDataFromState && !isLoading && !matchDetails)

  const detailsToggle = useLocalStorage('matchDetailView', true)
  const isDetailsView = detailsToggle.value
  const setIsDetailsView = detailsToggle.setValue
  const [isSummaryView, setIsSummaryView] = useState(true)
  const [isManageMode, setIsManageMode] = useState(false)
  const [isPriceBreakdownDrawerOpen, setIsPriceBreakdownDrawerOpen] = useState(false)
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)

  // Early returns for loading and error states
  if (isLoading) {
    return (
      <Vertical bg='default' style={{ height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <div>Loading match details...</div>
      </Vertical>
    )
  }

  if (hasNoValidMatchId) {
    return (
      <Vertical bg='default'>
        <NothingMessage title='No match selected' message='Please select a match from the list to view details.' />
      </Vertical>
    )
  }

  if (!matchDetails) {
    return (
      <Vertical bg='default'>
        <NothingMessage title='Match not found' message='The requested match could not be found.' />
      </Vertical>
    )
  }

  return (
    <Vertical style={{ height: '100%', overflow: 'hidden' }} bg='default'>
      {/* Navigation Header with Toggle Controls */}
      <MatchDetailNavigationHeader
        isDetailsView={isDetailsView}
        setIsDetailsView={setIsDetailsView}
        isSummaryView={isSummaryView}
        setIsSummaryView={setIsSummaryView}
        isManageMode={isManageMode}
        setIsManageMode={setIsManageMode}
        matchStatus={matchDetails?.status}
      />

      {/* Main Content Area */}
      <Horizontal flex={1} style={{ overflow: 'hidden' }}>
        {/* Left Sidebar */}
        {isDetailsView && (
          <MatchDetailSidebar
            match={matchDetails}
            matchActions={{
              handleIndividualHold,
              handleIndividualUnhold,
              handleIndividualUnapprove,
              handleIndividualApprove,
              handleVoidInvoice,
              handleUnvoidInvoice,
              approveMatchMutation,
              waitingOnSupplierMutation,
              voidInvoiceMutation,
              unvoidInvoiceMutation,
            }}
            metadata={metadataQuery.data}
            metadataLoading={metadataQuery.isLoading}
            modelConfig={modelConfig}
            getTimezoneForMatch={getTimezoneForMatch}
          />
        )}

        {/* Right Main Content Area */}
        <Vertical flex={isDetailsView ? 4 : 5} bg='default'>
          <Vertical
            style={{
              height: '100%',
              padding: '8px',
            }}
          >
            {/* All Grids Container */}
            <Vertical bg='default' border='all' borderRadius='6px' flex={1} style={{ overflow: 'hidden' }}>
              {isSummaryView && !isManageMode && (
                <MatchSummaryGrid
                  matchDetails={matchDetails}
                  pendingItems={[]}
                  onPerUnitClick={(priceId: string) => {
                    setSelectedPriceId(priceId)
                    setIsPriceBreakdownDrawerOpen(true)
                  }}
                  onMappingRefresh={handleMappingRefresh}
                />
              )}

              {/* Toggle between Tax Grids and Manage Match */}
              {isManageMode ? (
                <ManageMatchSection
                  matchDetails={matchDetails}
                  matchId={id!}
                  onExit={() => {
                    setIsManageMode(false)
                  }}
                  isSummaryView={isSummaryView}
                />
              ) : (
                <TaxGridsSection
                  matchDetails={matchDetails}
                  matchId={id!}
                  onManageMatch={() => setIsManageMode(true)}
                  onMappingRefresh={handleMappingRefresh}
                />
              )}
            </Vertical>
          </Vertical>
        </Vertical>
      </Horizontal>

      {/* Price Breakdown Drawer - Only render when needed */}
      {(isPriceBreakdownDrawerOpen || selectedPriceId) && (
        <PriceBreakdownDrawer
          isPriceBreakdownDrawerOpen={isPriceBreakdownDrawerOpen}
          setIsPriceBreakdownDrawerOpen={setIsPriceBreakdownDrawerOpen}
          selectedPriceId={selectedPriceId}
          setSelectedPriceId={setSelectedPriceId}
          matchDetails={matchDetails}
        />
      )}
    </Vertical>
  )
}
